import React, { useCallback, useState, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { FieldSetForm } from '@traquility/ui/diary';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../ui/common/Dropdown';
import type {
  FieldSetFormData,
  FieldSetFormLabels,
  FieldSetFormContainerProps,
  Category,
  FieldInSet,
  Goal,
} from '../shared/types/fieldSetForm';

export type { FieldSetFormContainerProps };

const CREATE_FIELD_SET_MUTATION = `
  mutation CreateFieldSet($input: CreateFieldSetInput!) {
    createFieldSet(input: $input) {
      fieldSet {
        id
        name
      }
      errors
    }
  }
`;

const UPDATE_FIELD_SET_MUTATION = `
  mutation UpdateFieldSet($input: UpdateFieldSetInput!) {
    updateFieldSet(input: $input) {
      fieldSet {
        id
        name
      }
      errors
    }
  }
`;

const DELETE_FIELD_SET_MUTATION = `
  mutation DeleteFieldSet($input: DeleteFieldSetInput!) {
    deleteFieldSet(input: $input) {
      success
      errors
    }
  }
`;

const CHANGE_FIELD_SET_CATEGORY_MUTATION = `
  mutation ChangeFieldSetCategory($input: ChangeFieldSetCategoryInput!) {
    changeFieldSetCategory(input: $input) {
      fieldSet {
        id
      }
      errors
    }
  }
`;

interface ChangeCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (categoryId: string) => void;
  categories: Category[];
  currentCategoryId?: string;
  labels: FieldSetFormLabels;
  isChanging: boolean;
}

function ChangeCategoryModal({
  isOpen,
  onClose,
  onConfirm,
  categories,
  currentCategoryId,
  labels: l,
  isChanging,
}: ChangeCategoryModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-folder me-2"></i>
              {l.changeCategoryTitle}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isChanging}></button>
          </div>
          <div className="modal-body">
            <div className="form-floating mb-3">
              <select
                className="form-select"
                id="new-category-select"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <option value="">— {l.selectCategory} —</option>
                {categories.map((cat) => (
                  <option
                    key={cat.id}
                    value={cat.id}
                    disabled={cat.id === currentCategoryId}
                    style={cat.id === currentCategoryId ? { fontStyle: 'italic', color: '#6c757d' } : {}}
                  >
                    {cat.name}{cat.id === currentCategoryId ? ` (${l.current})` : ''}
                  </option>
                ))}
              </select>
              <label htmlFor="new-category-select">{l.category}</label>
            </div>
            <p className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              {l.changeCategoryHelp}
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isChanging}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onConfirm(selectedCategoryId)}
              disabled={isChanging || !selectedCategoryId}
            >
              {isChanging ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.moveToCategory}
                </>
              ) : (
                l.moveToCategory
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ActivitiesCardProps {
  fields: FieldInSet[];
  addFieldUrl: string;
  labels: FieldSetFormLabels;
}

function ActivitiesCard({ fields, addFieldUrl, labels: l }: ActivitiesCardProps) {
  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center">
        <h5 className="card-title mb-0 me-auto">
          <i className="bi bi-check2-circle"></i>
          {' '}{l.activitiesTitle}
        </h5>
      </div>
      <div className="card-body">
        {fields.length > 0 && (
          <p className="text-secondary mb-3">{l.activitiesHelp}</p>
        )}

        {fields.length > 0 && (
          <ul className="list-group mb-3">
            {fields.map((field) => (
              <a
                key={field.id}
                href={field.editUrl}
                className="list-group-item list-group-item-action d-flex align-items-center"
              >
                <i className={`icon-flex icon-${field.icon} me-2`} style={{ width: 24, height: 24 }} />
                {field.name}
              </a>
            ))}
          </ul>
        )}

        <div className="text-end">
          <a href={addFieldUrl} className="btn btn-primary">
            <i className="bi bi-plus-circle"></i>
            {' '}{l.addActivity}
          </a>
        </div>
      </div>
    </div>
  );
}

interface GoalCardProps {
  goal?: Goal | null;
  labels: FieldSetFormLabels;
  goalNewUrl?: string;
  goalEditUrl?: string;
  goalDeleteUrl?: string;
  csrfToken: string;
}

function GoalCard({
  goal,
  labels: l,
  goalNewUrl,
  goalEditUrl,
  goalDeleteUrl,
  csrfToken,
}: GoalCardProps) {
  return (
    <div className="card my-4">
      <div className="card-header d-flex align-items-center">
        <h5 className="card-title mb-0 me-auto">
          <i className="bi bi-bullseye"></i>
          {' '}{l.goalTitle}
        </h5>
      </div>
      <div className="card-body">
        {goal ? (
          <div className="d-flex align-items-center mb-3">
            <div className="flex-grow-1">
              <div className="fw-bold">
                {l.goalTarget}: {goal.targetCount} {goal.period}
              </div>
              {goal.triggeredByFields && goal.triggeredByFields.length > 0 ? (
                <small className="text-muted">
                  {l.goalTriggeredBy}: {goal.triggeredByFields.join(', ')}
                </small>
              ) : (
                <small className="text-muted">{l.goalHelp}</small>
              )}
            </div>
            <div className="ms-3 d-flex gap-2">
              <a href={goalEditUrl} className="btn btn-sm btn-outline-primary" title={l.edit}>
                <i className="bi bi-pencil"></i>
                <span className="d-none d-sm-inline ms-1">{l.edit}</span>
              </a>
              <form action={goalDeleteUrl} method="post" style={{ display: 'inline' }}>
                <input type="hidden" name="_method" value="delete" />
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <button
                  type="submit"
                  className="btn btn-sm btn-outline-danger"
                  title={l.remove}
                  onClick={(e) => {
                    if (!confirm(l.goalTitle + '?')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <i className="bi bi-trash"></i>
                  <span className="d-none d-sm-inline ms-1">{l.remove}</span>
                </button>
              </form>
            </div>
          </div>
        ) : (
          <>
            <p className="text-muted mb-3">{l.goalHelp}</p>
            <a href={goalNewUrl} className="btn btn-primary">
              <i className="bi bi-plus-circle"></i>
              {' '}{l.setGoal}
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function FieldSetFormContainer({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
  mode,
  categories = [],
  fields = [],
  addFieldUrl,
  goal,
  goalNewUrl,
  goalEditUrl,
  goalDeleteUrl,
}: FieldSetFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChangeCategoryModal, setShowChangeCategoryModal] = useState(false);
  const [isChangingCategory, setIsChangingCategory] = useState(false);

  const handleSubmit = useCallback(async (data: FieldSetFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const isEdit = mode === 'edit';
    const mutation = isEdit ? UPDATE_FIELD_SET_MUTATION : CREATE_FIELD_SET_MUTATION;
    const variables = isEdit
      ? { input: { id: data.id, name: data.name } }
      : { input: { name: data.name, categoryId: data.categoryId } };

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: mutation, variables }),
      });

      if (!response.ok) {
        throw new Error('Network error');
      }

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateFieldSet : result.data.createFieldSet;

      if (mutationResult.errors && mutationResult.errors.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      // For create, redirect to edit page of the new field set
      if (!isEdit && mutationResult.fieldSet) {
        const newFieldSetId = mutationResult.fieldSet.id;
        // Replace the placeholder in successUrl with actual ID
        const redirectUrl = successUrl.replace('__ID__', newFieldSetId);
        window.location.href = redirectUrl;
      } else {
        window.location.href = successUrl;
      }
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl, mode]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleDelete = useCallback(async () => {
    if (!confirm(labels.delete + '?')) return;

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: DELETE_FIELD_SET_MUTATION,
          variables: { input: { id: initialData.id } },
        }),
      });

      const result = await response.json();

      if (result.errors || (result.data.deleteFieldSet.errors?.length > 0)) {
        const errorMessages = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.deleteFieldSet.errors;
        setErrors({ base: errorMessages });
        return;
      }

      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl, labels.delete]);

  const handleChangeCategory = useCallback(async (newCategoryId: string) => {
    setIsChangingCategory(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: CHANGE_FIELD_SET_CATEGORY_MUTATION,
          variables: { input: { id: initialData.id, newCategoryId } },
        }),
      });

      const result = await response.json();

      if (result.errors || (result.data.changeFieldSetCategory.errors?.length > 0)) {
        const errorMessages = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.changeFieldSetCategory.errors;
        setErrors({ base: errorMessages });
        setIsChangingCategory(false);
        setShowChangeCategoryModal(false);
        return;
      }

      // Reload page to show updated category
      window.location.reload();
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsChangingCategory(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id]);

  return (
    <>
      {/* Edit mode: More menu */}
      {mode === 'edit' && (
        <div className="position-absolute" style={{ top: 10, right: 10 }}>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-secondary">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              <DropdownItem onClick={() => setShowChangeCategoryModal(true)}>
                {labels.changeCategory}
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem className="text-danger" onClick={handleDelete}>
                {labels.delete}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      {/* Main form */}
      <FieldSetForm
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        errors={errors}
        labels={labels}
        mode={mode}
        isSubmitting={isSubmitting}
      />

      {/* Activities card (edit mode only) */}
      {mode === 'edit' && addFieldUrl && (
        <ActivitiesCard
          fields={fields}
          addFieldUrl={addFieldUrl}
          labels={labels}
        />
      )}

      {/* Goal card (edit mode only) */}
      {mode === 'edit' && goalNewUrl && (
        <GoalCard
          goal={goal}
          labels={labels}
          goalNewUrl={goalNewUrl}
          goalEditUrl={goalEditUrl}
          goalDeleteUrl={goalDeleteUrl}
          csrfToken={csrfToken}
        />
      )}

      {/* Change category modal */}
      <ChangeCategoryModal
        isOpen={showChangeCategoryModal}
        onClose={() => setShowChangeCategoryModal(false)}
        onConfirm={handleChangeCategory}
        categories={categories}
        currentCategoryId={initialData.categoryId}
        labels={labels}
        isChanging={isChangingCategory}
      />
    </>
  );
}

// Store root reference for cleanup
let formRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountFieldSetForm(
  container: HTMLElement,
  props: FieldSetFormContainerProps
): Root {
  if (formRoot) {
    formRoot.unmount();
  }

  const root = createRoot(container);
  formRoot = root;
  root.render(<FieldSetFormContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountFieldSetForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default FieldSetFormContainer;
